import SidebarLayout from "components/Layout/Layout";
import { useEffect, useState } from "react";
import type { CollapseProps } from "antd";
import { Collapse, ConfigProvider, Dropdown, Modal } from "antd";
import { API_URL } from "env";
import { useTheme } from "components/provider/theme";
import PermissionEditModal from "./PemissionEditModal";
import PermissionAddModal from "./PermissionAddModal";

type RoleDisplay = {
  id: string;
  displayName: string;
};

type Permission = {
  id: string;
  displayName: string;
  name: string;
  module: string;
  action: string;
  moduleDisplayName: string;
  actionDisplayName: string;
  roles_display: RoleDisplay[];
};

const PermissionPage = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [viewStyle, setViewStyle] = useState<"list" | "grid">("list");
  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<string[] | null>(null);
  const { theme, toggleTheme } = useTheme();

  async function fetchPermissions() {
    try {
      const response = await fetch(`${API_URL}api/v1/be/permissions`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      const permissions: Permission[] = data.data.items.map((item: any) => ({
        id: String(item.id),
        displayName: item.display_name,
        name: item.name,
        module: item.module ?? "",
        action: item.action,
        moduleDisplayName: item.module_display ?? "",
        actionDisplayName: item.action_display ?? "",
        roles_display: Array.isArray(item.roles_display)
          ? item.roles_display.map((role: any) => ({
              id: String(role.id),
              displayName: role.display_name,
            }))
          : [],
      }));
      setPermissions(permissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  }

  async function createPermission(module: string, action: string, displayName: string, is_active: boolean) {
    try {
      const response = await fetch(`${API_URL}api/v1/be/permissions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          module: module,
          action: action,
          display_name: displayName,
          is_active : is_active,
        }),
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      console.log("Permission created:", data);
    }
    catch (error) {
      console.error("Error creating permission:", error);
    }
  }

  useEffect(() => {
    fetchPermissions();
  }, []);

  // Group permissions by moduleDisplayName/module
  const groupedPermissions = Object.entries(
    permissions.reduce<Record<string, Permission[]>>((acc, permission) => {
      const key =
        permission.moduleDisplayName || permission.module || "Unknown Module";
      if (!acc[key]) acc[key] = [];
      acc[key].push(permission);
      return acc;
    }, {})
  );

  // Prepare Collapse panels
  const panels: CollapseProps["items"] = groupedPermissions.map(
    ([module, perms]) => ({
      key: module,
      label: (
        <div className="flex items-center justify-between">
          <span
            className={`text-xl font-bold ${
              theme === "light" ? "text-gray-900" : "text-gray-100"
            }`}
          >
            {module}
          </span>

          <Dropdown
            menu={{
              items: [
                {
                  key: "edit",
                  label: "Edit Module",
                  onClick: (e) => {
                    e.domEvent?.stopPropagation();
                    setIsModalEditOpen(true);
                    const permIds = perms.map((perm) => perm.id);
                    setEditingPermission(permIds);
                  },
                },
                {
                  key: "delete",
                  label: "Delete Module",
                  onClick: (e) => {
                    e.domEvent?.stopPropagation();
                    // handle delete module logic here
                    alert(`Delete module ${module}`);
                  },
                },
              ],
            }}
            trigger={["click"]}
          >
            <button
              className={`ml-2 p-1 rounded ${
                theme === "light" ? "hover:bg-gray-200" : "hover:bg-gray-700"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <svg
                width="20"
                height="20"
                fill="currentColor"
                className="text-gray-500"
                viewBox="0 0 20 20"
              >
                <circle cx="4" cy="10" r="2" />
                <circle cx="10" cy="10" r="2" />
                <circle cx="16" cy="10" r="2" />
              </svg>
            </button>
          </Dropdown>
        </div>
      ),
      children: (
        <ModulePermissions perms={perms} theme={theme} viewStyle={viewStyle} />
      ),
    })
  );

  return (
    <SidebarLayout
      title={
        <h4 className="text-2xl font-bold mb-4 text-gray-800 py-4">
          Permission Management
        </h4>
      }
    >
      <div className="flex justify-between mb-4">
        <button
          onClick={() => setIsModalCreateOpen(true)}
          className="px-4 py-2 bg-blue-600 !text-white rounded hover:bg-blue-700 transition-colors"
        >
          Add New Permission
        </button>
        <Modal title="Create New Permission" open={isModalCreateOpen} onCancel={() => setIsModalCreateOpen(false)} footer={null}>
            <PermissionAddModal/>
        </Modal>
        <Modal title="Edit Permission" open={isModalEditOpen} onCancel={() => setIsModalEditOpen(false)} footer={null}>
            <PermissionEditModal id={
              editingPermission ? editingPermission : []
            } />
        </Modal>
        <div className="flex items-center">
          <button
            className={`px-4 py-2 mr-2 rounded ${
              viewStyle === "list"
                ? "bg-blue-600 !text-white"
                : "bg-gray-200 !text-black"
            }`}
            onClick={() => setViewStyle("list")}
          >
            List View
          </button>
          <button
            className={`px-4 py-2 rounded ${
              viewStyle === "grid"
                ? "bg-blue-600 !text-white"
                : "bg-gray-200 !text-black"
            }`}
            onClick={() => setViewStyle("grid")}
          >
            Grid View
          </button>
        </div>
      </div>
      <ConfigProvider
        theme={{
          components: {
            Collapse: {
              headerBg: theme === "light" ? "#fff" : "#1f2937",
              contentBg: theme === "light" ? "#fff" : "#1f2937",
            },
          },
        }}
      >
        <Collapse items={panels} accordion />
      </ConfigProvider>
    </SidebarLayout>
  );
};

const ModulePermissions = ({
  perms,
  theme,
  viewStyle,
}: {
  perms: Permission[];
  theme: string;
  viewStyle: "grid" | "list";
}) => {
  return (
    <div
      className={
        viewStyle === "grid"
          ? `grid grid-cols-1 md:grid-cols-2 gap-2 `
          : ` flex flex-col gap-2`
      }
    >
      {perms.map((permission) => (
        <div
          key={permission.id}
          className={`mb-0 p-4 border rounded-lg shadow-sm relative ${
            theme === "light"
              ? "bg-white text-gray-900"
              : "bg-gray-900 text-gray-100"
          }`}
        >
          <h3
            className={`text-lg font-semibold mb-1 ${
              theme === "light" ? "text-blue-700" : "text-blue-300"
            }`}
          >
            {permission.displayName} - {`id :${permission.id}`}
          </h3>
          <span
            className={`mb-2 block ${
              theme === "light" ? "text-gray-600" : "text-gray-300"
            }`}
          >
            ({permission.moduleDisplayName} - {permission.actionDisplayName})
          </span>
          <div>
            <h4
              className={`text-md font-medium mb-1 ${
                theme === "light" ? "text-gray-800" : "text-gray-200"
              }`}
            >
              Assigned Roles:
            </h4>
            {permission.roles_display.length > 0 ? (
              <ul className="list-disc list-inside">
                {permission.roles_display.map((role) => (
                  <li
                    key={role.id}
                    className={
                      theme === "light" ? "text-gray-700" : "text-gray-300"
                    }
                  >
                    {role.displayName}
                  </li>
                ))}
              </ul>
            ) : (
              <p
                className={
                  theme === "light" ? "text-gray-500" : "text-gray-400"
                }
              >
                No roles assigned
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
export default PermissionPage;
